/**
 * MER-578: which part of the business a tile measures.
 *
 * Colour does two independent jobs on this dashboard and confusing them is how
 * the page ended up monochrome. --flag-* encodes STATE (is this number
 * healthy); the domain palette encodes DOMAIN (what the number is about).
 * MER-576 wired only state, so a page with no benchmark breaches rendered as
 * near-black text throughout -- the "too dark, needs colour across the board"
 * report.
 *
 * Assignment is DERIVED, never a hand-maintained per-widget list: Overview
 * tiles take their section's domain, every other tab takes its tab's domain.
 * A widget added for customer #7 inherits the right colour with no config,
 * which is the only version of this that survives 15 more customers.
 *
 * Plain .mjs so node:test imports it with no build step (priorWindow.mjs
 * precedent).
 */

export const DOMAINS = ["disposal", "operations", "financial", "safety"];

const BY_TAB = {
  "Cost & Disposal": "disposal",
  Operations: "operations",
  Benchmarking: "operations",
  "Data Quality": "operations",
  // MER-579: revenue is not its own domain. Everything on the money side
  // shares one hue, which is both honest and the only version that passes
  // all-pairs colour validation.
  Revenue: "financial",
  Profitability: "financial",
  "Sales Pipeline": "financial",
  "Billing & Collections": "financial",
  "Financial Health": "financial",
  "Safety & Fleet": "safety",
};

// Overview mixes every domain on one page, so its tiles key on their SECTION
// rather than the tab. Section prefixes come from SECTION_LABELS.Overview.
const BY_OVERVIEW_SECTION = {
  "L0.1": "disposal",
  "L0.2": "operations",
  "L0.3": "operations",
  "L0.4": "financial",
  "L0.5": "operations",
  // MER-577 folded the three working Executive Roll-Up tiles onto Overview.
  L6c: "financial",
};

/**
 * Resolve a widget to its domain key.
 *
 * @param {{id?: string, tab?: string, domain?: string}} widget
 * @returns {string} a key in DOMAINS; falls back to "operations" so an
 *   unmapped tab still gets a stable colour rather than an unstyled rail.
 */
export function widgetDomain(widget) {
  if (!widget) return "operations";
  // An explicit manifest value always wins, so a one-off can be overridden
  // without editing this file.
  if (widget.domain && DOMAINS.includes(widget.domain)) return widget.domain;

  const id = String(widget.id ?? "");
  if (widget.tab === "Overview") {
    // Longest prefix first: "L0.1" must beat a bare "L0".
    const keys = Object.keys(BY_OVERVIEW_SECTION).sort((a, b) => b.length - a.length);
    for (const prefix of keys) {
      if (id === prefix || id.startsWith(`${prefix}.`) || id.startsWith(prefix)) {
        return BY_OVERVIEW_SECTION[prefix];
      }
    }
    return "operations";
  }
  return BY_TAB[widget.tab] ?? "operations";
}

/** CSS custom-property reference for a domain's hue. */
export function domainVar(domain) {
  const key = DOMAINS.includes(domain) ? domain : "operations";
  return `var(--domain-${key})`;
}
